import { ServiceDefinition } from "@/Common/Api/Services/Dto/Responses/ServiceDefinition";
import { ServiceDefinitionDTO } from "@/Common/Api/Services/Dto/ServiceDefinitionDTO";
import { Guid } from "@/Common/Models/Guid";
import { ServiceComboTargetClientele } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { BaseServiceComboDefinitionModel } from "./BaseServiceDefinitionModel";
import { IServicePublishingConfig } from "./IServicePublishingConfig";

class ServiceDefinitionModel extends BaseServiceComboDefinitionModel {
    public Model: ServiceDefinition;
    public Employees: ServiceDefinitionModelEmployee[];
    public IsAdHoc: boolean;

    constructor(model: ServiceDefinition = null) {
        super();

        if (model == null) {
            this.Model = new ServiceDefinition();
            this.Model.OnlineClienenteleCible = 2; // set default client targets to "open"
            this.Model.SiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
        }
        else {
            this.Model = model;
            this.OnlineGroupId = model.OnlineGroupId;
            this.OnlineNameLookupId = model.OnlineNameLookupId;
            this.OnlineDescriptionLookupId = model.OnlineDescriptionLookupId;
            this.OnlineDisclaimerLookupId = model.OnlineDisclaimerLookupId;
            this.NotificationConfigurationId = model.NotificationConfigurationId;
        }

        this.Employees = [];
        this.InitLookup();
    }

    public ToCheckableUIModel(Target: CheckboxTarget): CheckboxUIModelT<ServiceDefinitionModel> {
        var serviceDef = new CheckboxUIModelT<ServiceDefinitionModel>();
        serviceDef.Model = this;
        serviceDef.Name = this.Name;
        serviceDef.Id = this.Model.Id;
        serviceDef.Target = Target;
        serviceDef.Checked = false;

        return serviceDef;
    }

    public GetFullName(): string {
        return this.Name + " - ( " + this.GetDefaultDurationMinutes() + " min. ) - " + cacheStore.GetLocalizedValueByLookupId(this.Model.SpecializationLookupId);
    }

    public GetDefaultDurationMinutes(): number {
        var DureeMin = 0;
        if (this.Model.DefaultDuration != null && this.Model.DefaultDuration > 0)
            DureeMin = this.Model.DefaultDuration / 600000000;

        return DureeMin;
    }

    public SetDurationMinutes(value: string): void {
        if (isNaN(Number(value)) == false)
            this.Model.DefaultDuration = Number(value) * 600000000;
        else
            throw new TypeError("Invalid Number " + value);
    }

    public InitLookup(): void {
        if (this.Model.NameLookupId == null)
            return;

        var Namelookup = cacheStore.GetLookupById(this.Model.NameLookupId);
        if (Namelookup != null) {
            this.NameFR = Namelookup.fr;
            this.NameEN = Namelookup.en;
            this.NameES = Namelookup.esMX;
            this.Name = Namelookup.GetValue();
        }
        else {
            this.NameFR = "Wait for next cache Refresh";
            this.NameEN = "Wait for next cache Refresh";
            this.NameES = "Wait for next cache Refresh";
            this.Name = "Wait for next cache Refresh";
        }

        var SpecializationLookup = cacheStore.GetLookupById(this.Model.SpecializationLookupId);
        this.IsAdHoc = (SpecializationLookup.LookupCode == "Ad-Hoc" || SpecializationLookup.LookupCode == "Ad-HocSpe" || SpecializationLookup.LookupCode == "");
    }

    public SortEmployees(): void {
        this.Employees = this.Employees.sortByProperty(employee => employee.NameWithPrefix);
    }

    public GetSite(): SitesModel {
        return cacheStore.GetSiteById(this.Model.SiteId);
    }

    public Clone(deepClone: boolean = true): ServiceDefinitionModel {
        var serviceCloneWithModels = new ServiceDefinitionDTO();
        $.extend(serviceCloneWithModels, JSON.parse(JSON.stringify(this.Model)));
        var clonedService = new ServiceDefinitionModel(serviceCloneWithModels);

        if (deepClone == true) {
            var clonedEmployees: ServiceDefinitionModelEmployee[] = [];
            this.Employees.forEach(x => {
                clonedEmployees.push(new ServiceDefinitionModelEmployee(x.ContactFacetId, x.NameWithPrefix));
            });

            clonedService.Employees = clonedEmployees;
        }

        clonedService.InitLookup();

        return clonedService;
    }

    public GetIsNotifyPatientDisabledByDefault(): boolean {
        if (this.Model)
            return this.Model.IsNotifyPatientDisabledByDefault;

        return false;
    }

    public SetIsNotifyPatientDisabledByDefault(val: boolean): void {
        if (this.Model)
            this.Model.IsNotifyPatientDisabledByDefault = val;
    }

    public GetUrgency(): boolean {
        if (this.Model)
            return this.Model.IsServiceUrgent;

        return false;
    }

    public SetUrgency(val: boolean): void {
        if (this.Model)
            this.Model.IsServiceUrgent = val;
    }

    public SetOnlineAvailability(val: boolean): void {
        if (this.Model)
            this.Model.IsAvailableOnline = val;
    }

    public GetOnlineAvailability(): boolean {
        if (this.Model)
            return this.Model.IsAvailableOnline;

        return false;
    }

    public SetOnlineClienenteleCible(val: ServiceComboTargetClientele): void {
        if (this.Model)
            this.Model.OnlineClienenteleCible = val;
    }

    public GetOnlineClienenteleCible(): ServiceComboTargetClientele {
        if (this.Model)
            return this.Model.OnlineClienenteleCible;

        return 0;
    }

    public SetPublishingConfig(val: IServicePublishingConfig): void {
        if (this.Model) {
            this.Model.PublishingRule = val.rule;
            this.Model.PublishingTimeframe = val.timeframe;
        }
    }

    public GetPublishingConfig(): IServicePublishingConfig | null {
        if (this.Model) {
            return {
                rule: this.Model.PublishingRule,
                timeframe: this.Model.PublishingTimeframe
            };
        }

        return null;
    }

    public SetSiteId(val: Guid): void {
        if (this.Model)
            this.Model.SiteId = val;
    }

    public GetSiteId(): Guid {
        if (this.Model)
            return this.Model.SiteId;

        return null;
    }

    public SetSpecializationId(val: string): void {
        if (this.Model)
            this.Model.SpecializationLookupId = val;
    }

    public GetSpecializationId(): string {
        if (this.Model)
            return this.Model.SpecializationLookupId;

        return "";
    }

    public SetLicenceOrganisationSpecialisationId(val: string): void {
        if (this.Model)
            this.Model.LicenceOrganisationSpecialisationId = val;
    }

    public GetLicenceOrganisationSpecialisationId(): string {
        if (this.Model)
            return this.Model.LicenceOrganisationSpecialisationId;

        return "";
    }

    public SetPrice(val: number): void {
        if (!isNaN(Number(val)) && this.Model)
            this.Model.Price = val;
        else
            throw new TypeError("Invalid Number " + val);
    }

    public GetPrice(): number {
        if (this.Model)
            return this.Model.Price > 0 ? this.Model.Price : 0;

        return 0;
    }

    public SetIsTeleconsultation(val: boolean): void {
        if (this.Model) {
            this.Model.IsTeleconsultation = val;

            // ONLY CHECK IF VALUE IS TRUE, DO NOTHING IF FALSE
            if (val)
                this.Model.IsNotifyPatientDisabledByDefault = !val;
        }
    }

    public GetIsTeleconsultation(): boolean {
        if (this.Model)
            return this.Model.IsTeleconsultation;

        return false;
    }

    public SetQuestionnaireId(val: string): void {
        if (this.Model)
            this.Model.QuestionnaireId = val;
    }

    public GetQuestionnaireId(): string {
        if (this.Model)
            return this.Model.QuestionnaireId;
    }
}

class ServiceDefinitionModelEmployee {
    public ContactFacetId: Guid;
    public NameWithPrefix: string;

    constructor(contactFacetId: Guid, nameWithPrefix: string) {
        this.ContactFacetId = contactFacetId;
        this.NameWithPrefix = nameWithPrefix;
    }

    public clone(): ServiceDefinitionModelEmployee {
        return JSON.parse(JSON.stringify(this));
    }
}

export { ServiceDefinitionModel, ServiceDefinitionModelEmployee };
